import { AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild} from '@angular/core';
import * as L from 'leaflet';
import { environment } from '../../environments/environment';
import { Bounds, InterpolationService, RangeStyle, PaletteService, ColourPalette, AnalyticsService, PaletteDescriptor } from 'map-wald';
import { LayerDescriptor, DisplaySettings,
         DisplaySettingsChange, LayerVariant, LayerSelection, DataOverlay, FlattenedLayerDescriptor, TimePeriod, LayerStyling, DataMode,
         DATA_MODE_CLASSES, ANOMALY_PALETTE_COUNT, ANOMALY_PALETTE_NAME, LEGEND_PRECISION,
         VectorLayerDescriptor, DATA_MODE_INVERSE_LABELS, DATA_MODE_NAMES} from '../data';
import { ConfigService } from '../config.service';
import { BaseMainMapComponent, OneTimeSplashComponent, BasemapDescriptor, PointMode, getFeatureBounds,
         Tour, TourService, FeatureUtils, LeafletMapComponent, DownloadService } from 'map-wald-leaflet';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LayersService } from '../layers.service';
import { PointDataService } from '../point-data.service';
import { delay, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { CacheService } from '../cache.service';
import { LegendUtils } from '../legend-utils';
import * as R from 'ramda';
import { ZonalService } from '../zonal.service';
import { ALL_POLYGON_MODES, CUSTOM_POLYGON, PolygonMode } from '../chart-panel/chart-panel.component';
import { WMSParameterService } from '../wms-parameter.service';
import { FeatureCollection, Point, Polygon } from 'geojson';
import { PaletteUtils, TimePeriodUtils } from '../utils';
import { ViewConfiguration, MapLoadingIndicators, MOBILE_TITLE_BAR_STYLES } from 'projects/map-wald-leaflet/src/lib';
import { ActivatedRoute } from '@angular/router';
import { CM_ANNUAL, CM_YR_ON_YR } from '../multi-year-timeseries-chart/multi-year-timeseries-chart.component';
import { DownloadFormComponent } from '../download-form/download-form.component';

const DATA_VIEW_SETTINGS = [
  "layer",
  "variable",
  "dataMode",
  "vectorLayer",
  "dataDisplay",
  "displayOpacity",
  "year",
  "month",
  "window",
  "basemap",
  "feature"
];

const MAP_VIEW_SETTINGS = [
  'lat',
  'lng',
  'zoom'
]

const POINT_METADATA_DELAY_MS=40000;
const MAX_SITES=environment.maxPointSites;
const COL_TRANSPARENT="#00000000";
const NULL_COLOUR="#00000000";

const SUPER2='²';
const DEGREE='°';
const DECIMAL_PLACES=1;
const FULL_EXTENT: Bounds = {
  west: -165,
  north: 40,
  south: -40,
  east: 165
};
const INITIAL_LONGITUDE_SPAN = 200;
const INITIAL_LATITUDE_SPAN = 80;

const DEFAULT_DELTA_OFFSET=-50;
const INITIAL_OPACITY=100;//%
const DEFAULT_PALETTE:PaletteDescriptor = {
  name:'YlOrBr',
  count:6,
  reverse:false
};

const USE_NORMAL_PALETTE:{[key in keyof typeof DataMode]?:boolean} = {
  [DataMode.NORMAL]:true,
  [DataMode.MEAN]:true
}

const DOWNLOAD_SERVICES = [
  {
    label: 'Image (PNG)',
    baseURL: environment.pngDownload,
    extension: 'png'
  },
  {
    label: 'Image (PDF)',
    baseURL: `${environment.pdfGeneration}/xx-map-download/landscape`,
    extension: 'pdf'
  },
  {
    label: 'Data (GeoTIFF)',
    baseURL: `${environment.geotiffDownload}`,
    extension: 'tif'
  },
  // {
  //   label: 'Data (NetCDF)',
  //   baseURL: null,
  //   extension: 'nc'
  // }
];

export interface FeatureStats {
  area: number;
  areaUnits: string;
}

@Component({
  selector: 'app-main-map',
  templateUrl: './main-map.component.html',
  styles: [
    MOBILE_TITLE_BAR_STYLES
  ],
  styleUrls: ['./main-map.component.scss'],
})
export class MainMapComponent extends BaseMainMapComponent implements AfterViewInit {

  feedbackAddress = environment.feedbackAddress;
  downloadServices = DOWNLOAD_SERVICES.slice();
  mapConfig = {
    debug: environment.debug,
    showDebugOverMap: environment.debugOverMap,
    screenshotMode: false,
    spatialDownloads: environment.spatialDownloads,
    validMapSelectionModes:ALL_POLYGON_MODES,
    latLngSelection: false,
    enableDrawing: false,
    showBoundaries: true,
    allowPolygonSelection: true,
    choropleth:false,
    showInlineChart: true,
    showLargeChart: false,
    showAdvanced: false,
    chartMode: CM_ANNUAL,
    showLegend: true,
    showScale: false,
  };

  selectedFeatureNumber = 0;

  pointMode = PointMode;
  selectedFeature: GeoJSON.Feature<GeoJSON.GeometryObject>;
  selectedFeatureID: string;
  selectedFeatureLabel: string;

  polygonFeaturesForSelectedPoint: GeoJSON.FeatureCollection<GeoJSON.Polygon>;

  opacity = INITIAL_OPACITY;

  customDownloadBounds: FeatureCollection<Polygon>;
  maxBounds = {...FULL_EXTENT};
  @ViewChild('theMap') map: LeafletMapComponent;
  dataOverlay: DataOverlay = {
    selection: {
      layer: null,
      variant: null,
      flattened: null,
      dataMode: DataMode.NORMAL
    },
    timePeriod: {
      from:null,
      to:null
    },
    wms: {
      tileID: null,
      tileURL: null,
      url: null,
      params: {}
    },
    choropleth:{
      styles: null,
      values: null
    },
    points:{
      features: null,
      displayFeatures: null,
      styles: {
        weight:1,
        radius:7,
        fillOpacity:100,
        color:'#00000077',
        fillColor:null
      }
    }
  };

  baseBoundaries = {
    layer: null as VectorLayerDescriptor,
    styles: {} as any
  };

  mapLayer: L.TileLayer;

  legend = LegendUtils.resetLegend();
  legendExplanation = '';
  legendUnits = '';
  legendTitleUnits = '';

  polygonMode: PolygonMode = 'predefined';

  layerVariants:LayerVariant[] = [];

  fragment: string;

  constructor(private appConfig: ConfigService,
              private modalService: NgbModal,
              private layersService: LayersService,
              private pointData: PointDataService,
              private palettes: PaletteService,
              private zonal: ZonalService,
              analytics: AnalyticsService,
              private element: ElementRef,
              private wms:WMSParameterService,
              private tours:TourService,
              private route: ActivatedRoute) {
    super(appConfig, appConfig,analytics);
    this.analytics.enabled = environment.trackActions;

    // this.tours.tours$.subscribe(tours=>{
    //   this.tour = tours[0].tour;
    // });

    // route.fragment.subscribe(fragment=>{
    //   this.fragment = fragment;
    // });

    this.setInitialBounds();

    this.appConfig.dashboards$.subscribe(dashboards=>{
      dashboards.forEach(d=>{
        if(d.options?.length){
          d.options.forEach(opt=>{
            let baseURL = `${environment.pdfGeneration}/${opt.dashboard}/${opt.orientation}/${opt.pages||1}`;
            this.registerDashboard({
              ...d,
              ...opt,
              baseURL
            });
            if(!environment.production){
              baseURL = `${environment.dashboard}/#/${opt.dashboard}/debug`;
              this.registerDashboard({
                ...d,
                ...opt,
                baseURL,
                label:`Debug ${opt.label||d.label}`
              });
            }
          });
        } else {
          this.registerDashboard(d);
        }
      });
      // this.downloadServices.push(...dashboards.map(d=>{
      //   const result = {...d};
      //   result.baseURL = `${environment.pdfGeneration}/${d.baseURL}`;
      //   return result;
      // }));
      // this.downloadServices = this.downloadServices.slice();
    });


    this.layersService.layerConfig$.subscribe(layers=>{
      const initialLayer = layers[0];
      this.forceMetadataQuery(layers.filter(l=>l.type==='point'));
    });

    this.route.queryParams.subscribe(params=>{
      if(Object.keys(params).length){
        this.applyTourView(params);
      }
    });

    if(!environment.production){
      this.downloadServices.unshift({
        label: 'Debug Dashboard',
        baseURL: `${environment.dashboard}/#/xx-map-download/debug`,
        extension: 'png'
      });
    }
  }

  forceMetadataQuery(pointLayers: LayerDescriptor[]) {
    setTimeout(()=>{
      pointLayers.forEach(l=>{
        let lyr = l as FlattenedLayerDescriptor;
        if(lyr.variants?.length){
          lyr = {...lyr,...lyr.variants[0]};
        }
        this.pointData.getValues(lyr,{},this.dataOverlay.timePeriod,DataMode.NORMAL,null).subscribe(data=>{
        });
      });
    },POINT_METADATA_DELAY_MS);
  }

  registerDashboard(d:DownloadService): void {
    this.downloadServices.push(d);
    this.downloadServices = this.downloadServices.slice();
  }

  setInitialBounds() {
    setTimeout(()=>{
      const el = this.element.nativeElement as HTMLElement;
      let aspectRatio = el.clientWidth / el.clientHeight;
      aspectRatio = Math.min(1, aspectRatio);
      const lonSpan = aspectRatio*0.5*INITIAL_LONGITUDE_SPAN;
      const latSpan = 0.5*INITIAL_LATITUDE_SPAN;
      this.bounds = {
        west: -lonSpan,
        east: lonSpan,
        north: latSpan,
        south: -latSpan
      };
    });
  }


  // @HostListener('window:resize', ['$event'])
  // onResize(event: Event): void {
  //   this.checkScreenSize();
  // }

  // private checkScreenSize(): void {
  //   this.isMobile = window.innerWidth < this.mobileBreakpoint;
  //   console.log('isMobile',this.isMobile);
  // }

  override ngOnInit(): void {
    super.ngOnInit();
    this.analytics.log('send','pageview');
  }

  onMobileTabChange(event: any) {
    // When switching to the chart tab (tab 2), trigger resize to fix chart width
    if (event.nextId === 2) {
      // Use setTimeout to ensure the DOM has updated before triggering resize
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 250);
    }
  }


  ngAfterViewInit(): void {
    this.map.withMap(m=>{
      this.zoom = m.getZoom();
      m.on('zoom',()=>{
        this.zoom = m.getZoom();
      });
    });
  }

  updateMapConfig(): void {
    const cfg = this.mapConfig;
    const pd = this.dataOverlay.selection.layer?.polygonDrill;
    const predefinedZonal = pd||this.dataOverlay.selection.layer;
    cfg.latLngSelection = pd && (this.polygonMode==='point');
    cfg.enableDrawing =   pd && (this.polygonMode==='draw');
    cfg.allowPolygonSelection = predefinedZonal && (this.polygonMode==='predefined');
    cfg.showBoundaries = true;

    cfg.validMapSelectionModes = ['point'];
    if(pd){
      cfg.validMapSelectionModes.push('draw');
    }
    if(predefinedZonal){
      cfg.validMapSelectionModes.push('predefined');
    }
    this.mapConfig = {...cfg};
  }

  variantChanged():void{
    this.setupDataOverlayLayer();
  }

  overlayReady(): boolean {
    return !!this.dataOverlay.selection?.layer &&
           !!this.dataOverlay.timePeriod?.from &&
           !!this.dataOverlay.timePeriod?.to;
  }

  setupDataOverlayLayer(): void {
    const layer = this.dataOverlay.selection.layer;
    const timePeriod = this.dataOverlay.timePeriod;

    if(!this.overlayReady()){
      return;
    }

    if(this.tour){

    }

    this.analytics.analyticsEvent('overlay', `${layer.label}:${(timePeriod.from as Date).toUTCString()}:${(timePeriod.to as Date).toUTCString()}`); // + ${this.relative?event.relativeVariable:'-'}

    this.dataOverlay.wms.params = null;
    this.dataOverlay.wms.url=null;

    if(this.mapConfig.choropleth){
      this.setupChoropleth();
    } else if(this.dataOverlay.selection.flattened.type==='grid'){
      this.dataOverlay.points.features = null;
      this.dataOverlay.points.displayFeatures = null;
      this.configureVectorLayer();
      this.setupWMSLayer();
    } else {
      this.configureVectorLayer();
      this.setupPointLayer();
    }
  }

  private flattenLayer(layer:LayerDescriptor,variant:LayerVariant):FlattenedLayerDescriptor {
    const result = Object.assign({},layer,variant);
    const INTERPOLATION_PARAMETERS = [
      'legendLabel'
    ];
    INTERPOLATION_PARAMETERS.forEach(p=>{
      const r = result as any;
      if(!r[p]){
        return;
      }
      r[p] = InterpolationService.interpolate(r[p],result);
    });
    return result;
  }

  private clearChoropleth():void{
    this.dataOverlay.choropleth.values = null;
    this.dataOverlay.choropleth.styles = null;
  }

  private setupChoropleth() {
    this.clearChoropleth();
    const layer = this.dataOverlay.selection.flattened;
    const mode = this.dataOverlay.selection.dataMode;
    this.loading(MapLoadingIndicators.LOAD_CHOROPLETH);
    this.zonal.getValues(layer,
                         this.boundaries.layer.zonal,
                         mode,
                         this.dataOverlay.timePeriod).subscribe(choroplethVals=>{
      this.dataOverlay.choropleth.values = choroplethVals;
      const style = this.makeLayerStyles(mode,layer.zonalStyles,this.dataOverlay.timePeriod);
      this.dataOverlay.choropleth.styles = style;
      this.updateLegend(style.values,style,true); // Typically provides 'null' for layer... Significant?

      this.configureVectorLayer();
      this.loaded(MapLoadingIndicators.LOAD_CHOROPLETH);
    },err=>{
      this.loaded(MapLoadingIndicators.LOAD_CHOROPLETH);
      this.mapConfig.choropleth = false;
    });
  }

  updateLegend(palette:string[],fill:RangeStyle<string>,computeLabels?:boolean) {
    this.describeLegend();
    const selection = this.dataOverlay.selection;
    this.legend = LegendUtils.makePointLegend(palette,
                                              fill,
                                              computeLabels?selection.flattened:null,
                                              this.legendUnits,
                                              LEGEND_PRECISION[selection.dataMode]);
  }

  describeLegend() {
    const description = LegendUtils.describeLegend(this.dataOverlay,this.mapConfig.choropleth);
    this.legendExplanation = description.explanation;
    this.legendUnits = description.units
    this.legendTitleUnits = description.titleUnits;
  }

  makeLayerStyles(mode:DataMode,s: LayerStyling,tp:TimePeriod,scale?:number): RangeStyle<string> {
    scale = scale || 1;
    const numMonths = TimePeriodUtils.durationInMonths(tp);
    const baseClasses = PaletteUtils.effectiveClasses(s,numMonths,scale);
    if(USE_NORMAL_PALETTE[mode]){
      const colours = this.palettes.getLocalPalette(
        s?.palette?.name||'Blues',
        s?.palette?.reverse??false,
        s?.palette?.count||8);

      if(s?.below==='transparent'){
        colours.unshift(COL_TRANSPARENT);
      }
      if(s?.above==='transparent'){
        colours.push(COL_TRANSPARENT);
      }

      return new RangeStyle(null,colours,baseClasses,NULL_COLOUR);
        //[0,5,25,50,100,200,300,500])
    }

    const colours = this.palettes.getLocalPalette(ANOMALY_PALETTE_NAME,s.reverseAnomalyPalette,ANOMALY_PALETTE_COUNT);
    if(((mode===DataMode.ABS_MEAN) || (mode===DataMode.TREND))&&baseClasses?.length){
      if(mode===DataMode.TREND){
        scale *= s.trendScale || 0.02;
      }
      const classes:number[] = PaletteUtils.makeAbsAnomalyClasses(s,numMonths,scale);
      return new RangeStyle(null,colours,classes,NULL_COLOUR);
    }

    return new RangeStyle(null,colours,DATA_MODE_CLASSES[mode],NULL_COLOUR);
    // if(mode===DataMode.REL_MEAN){
    //   return new RangeStyle(null,colours,REL_MEAN_CLASSES)
    // }

    // throw new Error('Unsupported data mode');
    // return null;
  }

  private setupPointLayer(): void {
    this.loading(MapLoadingIndicators.LOAD_POINT);
    const selection = this.dataOverlay.selection;
    let palette = selection.flattened.pointStyles.palette || DEFAULT_PALETTE;
    // if(this.mapRelativeMode){
    //   palette = this.dataOverlay.selection.layer.relativeOptions[this.mapRelativeMode]?.palette || palette;
    // }
    const fromDate = this.dataOverlay.timePeriod.from;
    const toDate = this.dataOverlay.timePeriod.to;
    const palette$ = USE_NORMAL_PALETTE[selection.dataMode]?
      this.palettes.getPalette(palette.name,palette.reverse,palette.count):
      this.palettes.getPalette('RdYlBu',false,11);
    forkJoin([
      this.pointData.getValues(selection.flattened,{},this.dataOverlay.timePeriod,selection.dataMode,null),
      palette$
    ]).subscribe(([features,palette]) => {
      if((this.dataOverlay.timePeriod.from!==fromDate)||
         (this.dataOverlay.timePeriod.to!==toDate)) {
        return;
      }

      this.dataOverlay.points.features = features;
      this.dataOverlay.points.features.features = this.dataOverlay.points.features.features.filter(f=>!isNaN(f.properties.value));
      this.selectPointFeatures();
      this.configurePointLegend(palette);
      this.loaded(MapLoadingIndicators.LOAD_POINT);
    });
  }

  selectPointFeatures() {
    const pointSelection = this.dataOverlay.points;
    if(!pointSelection.features?.features?.length){
      pointSelection.displayFeatures = null;
      return;
    }
    pointSelection.displayFeatures = {
      type: 'FeatureCollection',
      features: pointSelection.features.features.slice()
    };

    let rankThreshold = MAX_SITES*8;
    if(pointSelection.displayFeatures.features.length>MAX_SITES){
      pointSelection.displayFeatures.features =
        pointSelection.displayFeatures.features.filter(f=>pointWithinBounds(f.geometry,this.bounds));
      rankThreshold = Math.min(rankThreshold,pointSelection.displayFeatures.features.length);
    }
    while(pointSelection.displayFeatures.features.length>MAX_SITES){
      pointSelection.displayFeatures.features =
        pointSelection.displayFeatures.features.filter(f=>f.properties.limitVariableRank<=rankThreshold);
      rankThreshold *= 0.5;
    }
  }

  private setupWMSLayer(): void {
    this.wms.getParameters(this.dataOverlay.selection,this.dataOverlay.timePeriod,this.opacity).subscribe(params=>{
      this.dataOverlay.wms = params;
      this.configureWMSLegend();
    });
  }

  configurePointLegend(palette:ColourPalette): void {
    // palette = palette.map(c=>setAlpha(c,this.opacity*0.01));
    let breaks: number[];
    const selection = this.dataOverlay.selection;
    if(DATA_MODE_CLASSES[selection.dataMode]){
      breaks = DATA_MODE_CLASSES[selection.dataMode];
    } else if(selection.flattened.pointStyles.classes){
      breaks = selection.flattened.pointStyles.classes;
      if((selection.dataMode===DataMode.ABS_MEAN||(selection.dataMode===DataMode.TREND))){
        const scale = (selection.dataMode===DataMode.ABS_MEAN) ? 1 : 0.02;
        const max = (breaks[breaks.length-1] || breaks[breaks.length-2]) * 0.5 * scale;
        breaks = PaletteUtils.makeAbsAnomalyClasses(selection.flattened.pointStyles,TimePeriodUtils.durationInMonths(this.dataOverlay.timePeriod));
      }
    } else {
      const values:number[] = (this.dataOverlay.points.features.features).map(f=>f.properties.value).filter(v=>!isNaN(v));
      const max = Math.max(...values);
      const min = Math.min(...values);
      const count = palette.length+1;
      if(min>=0){
        breaks = R.range(0,count+1).map(v=>0.5*max*v/(count-1));
      } else {
        const absMax = Math.max(Math.abs(min),Math.abs(max));
        breaks = R.range(0,count).map(v=>-absMax+2*absMax*v/(count-1));
      }
      //[0, max/10, 2*max/10, 3*max/10, 4*max/10, 5*max/10];
    }

    this.dataOverlay.points.styles.fillColor = new RangeStyle('value',palette,breaks,NULL_COLOUR);
    // this.siteSize = new RangeStyle('value',[1,2,3,5,8,13,21],breaks);
    // this.dataOverlay.points.styles.radius = 5;//new RangeStyle('value',[5,5,5,5,5,5,5],breaks);
    this.updateLegend(palette,this.dataOverlay.points.styles.fillColor as RangeStyle<string>);
  }

  configureWMSLegend(): void {
    this.legend = LegendUtils.resetLegend();
    this.legendExplanation = '';

    this.layersService.getLayerMetadata(this.dataOverlay.selection.flattened).subscribe(metadata=>{
      if(!metadata){
        return;
      }
      const styles = this.dataOverlay.selection.flattened.gridStyles;
      // if(styles){
      const mode = this.dataOverlay.selection.dataMode;
      const style = this.makeLayerStyles(mode,styles,this.dataOverlay.timePeriod,this.dataOverlay.selection.flattened.scale);
      this.updateLegend(style.values,style);
      // } else {
      //   // Unused
      //   this.legend = LegendUtils.makeLegend(metadata,this.dataOverlay.selection.flattened.legendLabels);
      // }
    });
  }

  mapSettingChanged(event:DisplaySettingsChange): void {
    if((this as any)[event.setting]!==undefined){
      (this as any)[event.setting] = event.value;
    }

    this.analytics.analyticsEvent(event.setting,event.value);

    if(event.setting==='opacity'){
      this.setOpacity();
    }

    if(event.setting==='vectorLayer'){
      this.configureVectorLayer(event.value);
      if(this.mapConfig.choropleth){
        this.updateOverlay();
      }
    }

    if(event.setting==='choropleth'){
      this.mapConfig.choropleth = event.value;
      this.updateOverlay();
    }

    if(event.setting==='showLegend'){
      this.mapConfig.showLegend = event.value;
    }

    if(event.setting==='showScale'){
      this.mapConfig.showScale = event.value;
    }

    if(event.setting==='closePanel'){
      this.mapConfig.showAdvanced = false;
      this.mapConfig.showInlineChart = !this.mapConfig.showLargeChart;
    }
  }

  private setLayer(layer: LayerDescriptor) {
//#region delete
    if(this.dataOverlay.selection.layer===layer){
      return;
    }

    this.dataOverlay.selection.layer = layer;
    this.layerVariants = layer?.variants||[];
    this.dataOverlay.selection.variant = this.layerVariants[0];
//#endregion


    this.setupDataOverlayLayer();

    if(!this.dataOverlay.selection.layer?.polygonDrill){
      this.polygonMode = 'point';
    }


    this.updateMapConfig();
  }

  displayOptionsChanged(event: DisplaySettings): void {
    this.setOpacity();

    if(event.resetBounds){
      this.resetBounds();
    }

    if(event.showWindows!==undefined){
      this.showWindows = event.showWindows;
    }

    if(event.basemap){
      this.basemap = event.basemap;
      this.basemapChanged();
    }

    if(event.vectorLayer!==undefined){
      setTimeout(()=>{
        this.configureVectorLayer(event.vectorLayer);
      });
    }
  }

  pointFeatureSelected(geoJSON: any): void {
    this.setSelectedFeature(geoJSON);
  }

  pointSelected(latlng: L.LatLng): void {
    if(!this.mapConfig.latLngSelection){
      this.clearSelection();
      return;
    }

    const f = FeatureUtils.makeSquareFeature(latlng,this.dataOverlay?.selection?.flattened?.resolution);
    f.properties.source = CUSTOM_POLYGON;
    f.properties.chartLabel = `Selected point: ${latlng.lat.toFixed(2)}${DEGREE}, ${latlng.lng.toFixed(2)}${DEGREE}`
    this.polygonFeaturesForSelectedPoint = FeatureUtils.makeFeatureCollection(f)
    this.setSelectedFeature(f);
  }

  polygonFeatureSelected(geoJSON: any): void {
    this.selectedFeatureNumber++;
    const currentSelection = this.selectedFeatureNumber;
    const drawn = this.mapConfig.enableDrawing;
    const polygonSource = drawn?CUSTOM_POLYGON:this.boundaries.layer.name;
    if(drawn){
      geoJSON.properties.chartLabel = 'Custom drawn polygon';
    }

    if(this.mapConfig?.debug&&!drawn&&this.dataOverlay.choropleth.values){
      const keyField = this.boundaries.layer.tileLayers[0].keyField;
      const keyVal = geoJSON.properties[keyField];
      const val = this.dataOverlay.choropleth.values[keyVal];
      console.log(`Selected ${keyField}=${keyVal} with value ${val}`);
    }

    geoJSON.properties.source = polygonSource;
    this.analytics.analyticsEvent('select-polygon',`${polygonSource}`);

    const needGeoJson = !drawn&&this.boundaries.layer.tiles; // &&!this.dataOverlay.selection.flattened.zonal

    setTimeout(()=>{
      if(this.selectedFeatureNumber!==currentSelection){
        return;
      }

      this.setSelectedFeature(geoJSON);
    });
  }

  mapDoubleClick(event:any): void {
    setTimeout(()=>{
      if(!this.selectedFeature){
        return;
      }
      this.bounds = getFeatureBounds(this.selectedFeature);
    });
  }

  setSelectedFeature(feature: GeoJSON.Feature<GeoJSON.GeometryObject>) {
    if(feature!==this.polygonFeaturesForSelectedPoint?.features[0]){
      this.polygonFeaturesForSelectedPoint = null;
    }

    setTimeout(()=>{
      this.selectedFeature = feature;
      if(this.polygonMode==='predefined'){
        const idField = this.boundaries.layer.tileLayers[0].keyField
        this.selectedFeatureID = feature?.properties[idField];
        const labelFormat = this.boundaries.layer.label;
        this.selectedFeatureLabel = InterpolationService.interpolate(labelFormat,feature?.properties);
      } else {
        this.selectedFeatureID = null;
        this.selectedFeatureLabel = null;
      }
    });
    // this.featureStats = calcFeatureStats(feature);

    // this.clearChart();
    // this.chartPolygonTimeSeries();
  }

  clearSelection(){
    this.setSelectedFeature(null);
    this.configureVectorLayer();
  }

  resetBounds(): void {
    this.bounds = {...FULL_EXTENT};
  }

  override configureVectorLayer(l?: VectorLayerDescriptor): void {
    super.configureVectorLayer(l);
    // const target = this.boundaries;
    // if(!l){
    //   l = target.layer;
    // }
    // if(target.layer && (target.layer.name!==l?.name)){
    //   this.analytics.analyticsEvent('vector', l.name);
    // }

    // target.layer = l;
    if(!l||!l.tileLayers){
      return;
    }

    // target.styles = {};
    // l.tileLayers.forEach(tl=>{
    //   target.styles[tl.layer] = (feature:any)=>{
    //     let result:any = {
    //       fill:true,
    //       fillOpacity:0.0,
    //       ...l.styles||{}
    //     };
    //     if(this.mapConfig.choropleth&&this.dataOverlay.choropleth.values){
    //       const key = feature[target.layer.keyField||target.layer.tileLayers[0].keyField];
    //       const val = this.dataOverlay.choropleth.values[key];

    //       result.fillOpacity = this.opacity*0.01;
    //       result.fill=true;
    //       if(this.dataOverlay.choropleth.styles){
    //         result.fillColor = this.dataOverlay.choropleth.styles.getStyleForValue(val);
    //       }
    //     }
    //     return result;
    //   }
    // });

    if(l.alsoShow?.length){
      const alsoShow = l.alsoShow[0];
      if(this.baseBoundaries.layer?.filename!==alsoShow){
        this.appConfig.vectorLayers$.subscribe(vls=>{
          const vl = vls.find(v=>v.filename===alsoShow);
          if(vl){
            this.baseBoundaries.layer = vl;
            this.baseBoundaries.styles = {};
            vl.tileLayers.forEach(tl=>{
              this.baseBoundaries.styles[tl.layer] = vl.styles;
            });
          }
        });
      }
    } else {
      this.baseBoundaries.layer = null;
    }
  }

  override applyCustomVectorStyling(layer:VectorLayerDescriptor,result: any, feature: any): void {
    if(!this.mapConfig.choropleth||!this.dataOverlay.choropleth.values){
      return;
    }
    const key = feature[layer.keyField||layer.tileLayers[0].keyField];
    const val = this.dataOverlay.choropleth.values[key];

    result.fillOpacity = this.opacity*0.01;
    result.fill=true;
    if(this.dataOverlay.choropleth.styles){
      result.fillColor = this.dataOverlay.choropleth.styles.getStyleForValue(val);
    }
  }


  setOpacity(): void {
    const opacityFraction = this.opacity*0.01;
    this.dataOverlay.wms.params = Object.assign({},this.dataOverlay.wms.params,{opacity:opacityFraction});
    this.configureVectorLayer();
    this.dataOverlay.points.styles = {...this.dataOverlay.points.styles,fillOpacity:opacityFraction,opacity:2*opacityFraction};
  }

  vectorLayerChanged(vl: VectorLayerDescriptor): void {
    if((this.polygonMode==='predefined')&&
    !vl?.source&&
    !vl?.tileLayers) {
      setTimeout(()=>{
        this.polygonMode='point';
        this.updateMapConfig();
      })
    }

    this.configureVectorLayer(vl);
    if(this.mapConfig.choropleth){
      this.setupDataOverlayLayer();
    }
  }

  featureSelectionModeChanged(): void {
    this.ensureActiveVectorLayer();
    this.updateMapConfig();
  }

  ensureActiveVectorLayer() {
    // if((this.polygonMode==='predefined')&&
    //    !this.boundaries.layer?.source&&
    //    !this.boundaries.layer?.tileLayers&&
    //    this.vectorLayers?.length){
    //       setTimeout(()=>{
    //         const newLayer = this.vectorLayers.find((l)=>l.source||l.tileLayers);
    //         this.configureVectorLayer(newLayer);
    //       });
    //    }
  }

  closeAbout(): void {
    this.splash.close();
  }

  showAbout(event: any): void{
    event.stopPropagation();
    event.preventDefault();
    this.splash.show();
  }

  choroplethToggled(): void {
    setTimeout(()=>{
      this.configureVectorLayer();
      this.setupDataOverlayLayer();
    });
  }

  layerSelectionChanged(evt:LayerSelection):void {
    setTimeout(()=>{
      if(evt.dataMode!==this.dataOverlay.selection.dataMode){
        this.analytics.analyticsEvent('dataMode',DATA_MODE_NAMES[evt.dataMode]);
      }

      this.dataOverlay.selection = {
        layer:evt.layer,
        variant:evt.variant,
        flattened:this.flattenLayer(evt.layer,evt.variant),
        dataMode:evt.dataMode
      };
      this.updateOverlay();
    });
  }

  polygonModeChanged(mode:PolygonMode){
    this.polygonMode = mode;
    this.updateMapConfig();
  }

  chartModeChanged(mode:string){
    this.mapConfig.chartMode = mode;
  }

  timePeriodChanged(evt:TimePeriod):void {
    this.dataOverlay.timePeriod=evt;
    this.updateOverlay();
  }

  updateOverlay() {
    this.dataOverlay = Object.assign({},this.dataOverlay);
    this.setupDataOverlayLayer();
    this.updateMapConfig();
  }

  override mapBoundsChanged(bounds:Bounds){
    super.mapBoundsChanged(bounds);
    this.selectPointFeatures();
  }

  forceRefresh(): void {
    this.map.withMap(m=>{
      const zoom = m.getZoom();
      m.setZoom(zoom);
      this.bounds = {...this.bounds};
    });
  }

  wmsLoading(): void {
    this.loading(MapLoadingIndicators.LOAD_WMS);
  }

  wmsLoaded(): void {
    this.loaded(MapLoadingIndicators.LOAD_WMS);
  }

  chartLoadingStarted(): void {
    this.loading(MapLoadingIndicators.LOAD_TIME_SERIES);
  }

  chartLoadingFinished(): void {
    this.loaded(MapLoadingIndicators.LOAD_TIME_SERIES);
  }

  downloadBoundsChanged(bounds:Bounds): void {
    if(!bounds){
      this.customDownloadBounds = null;
      return;
    }

    this.customDownloadBounds = {
      type:'FeatureCollection',
      features:[
        {
          type:'Feature',
          geometry:{
            type:'Polygon',
            coordinates:[[
              [bounds.west,bounds.north],
              [bounds.west,bounds.south],
              [bounds.east,bounds.south],
              [bounds.east,bounds.north],
              [bounds.west,bounds.north]
            ]],
          },
          properties:{} as any
        }
      ]
    };
  }

  tourClosed(): void {
    this.tour = null;
  }

  applyTourView(newView:ViewConfiguration): void {
    let timer = 0;
    if(!this.overlayReady()){
      // console.log('Delaying tour view. Waiting for map');
      timer = 500;
    }
    setTimeout(()=>{
      if(this.overlayReady()){
        this._applyTourview(newView);
      } else {
        // console.log('Map still not ready. Delaying tour view');
        this.applyTourView(newView);
      }
    },timer);
  }

  viewChangeCount = 0;
  _applyTourview(newView:ViewConfiguration): void {
    if(!this.overlayReady()){
      throw new Error('Cannot apply tour view. Map not ready');
    }

    if(newView.zoom){
      this.map.applyExtent(newView);
    }

    // console.log('VC: Applying view',newView);
    const ORDER = DATA_VIEW_SETTINGS;
    let viewChange = of(null);
    ORDER.forEach((key)=>{
      if(newView[key]!==undefined){
        viewChange = viewChange.pipe(
          switchMap(()=>{
            return this.applyTourViewChange(key,newView[key]);
          })
        );
      }
    });
    viewChange.subscribe(()=>{
      // console.log('VC: View change complete');
      // console.log(this.dataOverlay);
      this.layerSelectionChanged(this.dataOverlay.selection);
      setTimeout(()=>{
        this.viewChangeCount++;
      },1000);
    });
  }

  applyTourViewChange(key: string, arg1: string | number | boolean): Observable<any> {
    switch(key){
      case 'layer':
        // console.log('VC: Applying layer',arg1);
        return this.layersService.matchingLayers({label:arg1 as string}).pipe(
          map(layers=>layers[0]),
          tap(layer=>{
            this.dataOverlay.selection = {
              ...this.dataOverlay.selection,
              layer:layer
            };
            if(!layer.variants){
              this.dataOverlay.selection.variant = null;
            }
          })
        );
      case 'variable':
        // console.log('VC: Applying variable',arg1);
        this.dataOverlay.selection = {
          ...this.dataOverlay.selection,
          variant:arg1?this.dataOverlay.selection.layer.variants.find(v=>v.variantLabel===arg1):null
        };
        return of(null);
      case 'dataMode':
        // console.log('VC: Applying dataMode',arg1);

        this.dataOverlay.selection = {
          ...this.dataOverlay.selection,
          dataMode:DATA_MODE_INVERSE_LABELS[arg1 as string]
        };
        return of(null);
      case 'vectorLayer':
        // console.log('VC: Applying vectorLayer',arg1);
        return this.appConfig.vectorLayers$.pipe(
          map(layers=>layers.find(v=>v.name===arg1)),
          tap(layer=>this.configureVectorLayer(layer))
        );
      case 'dataDisplay':
        // console.log('VC: Applying dataDisplay',arg1);
        if(arg1==='false'){
          arg1 = false;
        }
        this.mapConfig.choropleth = arg1 as boolean;
        return of(null);
      case 'displayOpacity':
        // console.log('VC: Applying displayOpacity',arg1);
        this.opacity = +arg1 as number;
        return of(null);
      case 'year':
        // console.log('VC: Applying year',arg1);
        this.dataOverlay.timePeriod = TimePeriodUtils.moveToyear(this.dataOverlay.timePeriod,+arg1 as number);
        return of(null);
      case 'month':
        // console.log('VC: Applying month',arg1);
        this.dataOverlay.timePeriod = TimePeriodUtils.moveToMonth(this.dataOverlay.timePeriod,(+arg1 as number)-1);
        return of(null);
      case 'window':
        // console.log('VC: Applying window',arg1);
        this.dataOverlay.timePeriod = TimePeriodUtils.setDuration(this.dataOverlay.timePeriod,+arg1 as number);
        return of(null);
      case 'basemap':
        // console.log('VC: Applying basemap',arg1);
        return this.appConfig.basemaps$.pipe(
          map(basemaps=>basemaps.find(b=>b.name===arg1 as string)),
          tap(basemap=>this.basemap = basemap)
        );
      case 'feature':
        // console.log('VC: Applying feature',arg1);
    }

    return of(null).pipe(
      delay(100),
      // tap(_=>console.log(`VC: Unknown ${key}=${arg1}`))
    );
  }

  getView(): any {
    const result:any = {};
    result.layer = this.dataOverlay.selection.layer?.label;
    result.variable = this.dataOverlay.selection.variant?.variantLabel;
    result.dataMode = DATA_MODE_NAMES[this.dataOverlay.selection.dataMode];
    result.displayOpacity = this.opacity;
    const centre = this.map.map.getCenter();
    result.lat = centre.lat;
    result.lng = centre.lng;
    result.zoom = this.map.zoom;
    result.year = this.dataOverlay.timePeriod.to.getUTCFullYear();
    result.month = this.dataOverlay.timePeriod.to.getUTCMonth()+1;
    result.window = TimePeriodUtils.durationInMonths(this.dataOverlay.timePeriod);
    result.dataDisplay = this.mapConfig.choropleth;
    result.vectorLayer = this.boundaries.layer?.name;
    result.basemap = this.basemap.name;
    result.feature = this.selectedFeatureID;
    return result;
  }

  copyViewSettings(): void {
    const view = this.getView();
    const viewString = JSON.stringify(view);
    navigator.clipboard.writeText(viewString).then(()=>{
      // console.log('VC: copied');
    });
  }

  copySharingLinkToClipboard(): void {
    const view = this.getView();
    const url = `${window.location.origin}${window.location.pathname}?` + Object.keys(view).map(k=>`${k}=${encodeURIComponent(view[k])}`).join('&');
    navigator.clipboard.writeText(url).then(()=>{
      // console.log('copied');
      this.analytics.analyticsEvent('copy-link',url);
    });
  }

  tourStartedFromButtonBar(tour:Tour): void {
    this.tour = tour;
    this.analytics.analyticsEvent('tour',tour.title);
  }

  copyWMSExpression(): void {
    const wms = this.dataOverlay.wms;
    const expression = wms.params.expression;
    navigator.clipboard.writeText(expression).then(()=>{
      // console.log('copied');
    });
  }

  toggleAdvancedMapSettings(): void {
    this.mapConfig.showAdvanced = !this.mapConfig.showAdvanced;
    this.mapConfig.showInlineChart = !this.mapConfig.showAdvanced && !this.mapConfig.showLargeChart;
  }

  chartExpanded(): void {
    this.mapConfig.showInlineChart = false;
    this.mapConfig.showLargeChart = true;
  }

  chartCollapsed(): void {
    this.mapConfig.showInlineChart = true;
    this.mapConfig.showLargeChart = false;
  }

  showMapDownload(): void {
    const ref = this.modalService.open(DownloadFormComponent,{
      size:'lg'
    });
    const component = ref.componentInstance as DownloadFormComponent;
    component.mapConfiguration = this.dataOverlay;
    component.choroplethMode = this.mapConfig.choropleth;
    component.boundaries = this.boundaries.layer;
    component.feature = this.selectedFeature;
    component.bounds = this.bounds;
    component.featureLabel = this.selectedFeatureLabel;
    component.closed.subscribe(()=>{
      ref.close();
    });
    // Show map download modal
  }
}

function pointWithinBounds(geometry: Point, bounds: Bounds): boolean {
  return geometry.coordinates[0]>=bounds.west&&
    geometry.coordinates[0]<=bounds.east&&
    geometry.coordinates[1]>=bounds.south&&
    geometry.coordinates[1]<=bounds.north;
}

function setAlpha(colour: string, alpha: number): string {
  return colour.replace(')',`,${alpha})`).replace('rgb(','rgba(');
}


